import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@eriko/ui/src/components/ui/card';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Separator } from '@eriko/ui/src/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@eriko/ui/src/components/ui/dialog';
import { Link2, Unlink, RefreshCw, RefreshCcw, CheckCircle } from 'lucide-react';
import {
  useAccountingStatus,
  useConnectQBO,
  useConnectXero,
  useDisconnectAccounting,
  useSyncAccounting,
  useRefreshOnSyncComplete,
} from '../../../hooks/useAccounting';
import { PaymentSettingsCard } from './PaymentSettingsCard';
import { resolveActiveKind, type SyncKind } from './syncButtonState';

function ConnectedState() {
  const { t } = useTranslation('settings');
  const { data: status } = useAccountingStatus();
  const disconnect = useDisconnectAccounting();
  const sync = useSyncAccounting();
  const [confirmOpen, setConfirmOpen] = useState(false); // disconnect confirmation

  if (!status) return null;

  const isSyncing = !!status.isSyncing;
  const paused = !!status.paused;
  // A job is "in progress" while RUNNING/queued OR paused (error backoff / Xero
  // daily-budget pause). Keying the button only off `isSyncing` made it oscillate
  // "Syncing… → Sync now → Syncing…" across backoff cycles (a paused job has
  // isSyncing=false), so treat the whole active lifecycle as busy.
  const inProgress = isSyncing || paused;
  const busy = sync.isPending || inProgress; // both buttons disabled while busy

  // The button the user CLICKED owns the "Syncing…" label for the whole active
  // session. `sync.variables` (the boolean passed to mutate: true=FULL) survives
  // the isPending→isSyncing handoff in TanStack Query, so the label stays on the
  // clicked button instead of flipping to the OTHER one via the running job's
  // kind — clicking "Sync now" while a FULL job is already active (enqueue
  // returns that FULL job) previously moved "Syncing…" onto "Re-sync all".
  const clickedKind: SyncKind | null =
    sync.variables === undefined ? null : sync.variables ? 'FULL' : 'INCREMENTAL';
  // Only the clicked button is labelled; an un-clicked (connect-triggered /
  // reloaded) sync shows progress in the header instead — never a wrong button.
  const activeKind = resolveActiveKind({ busy, clickedKind });
  const activelyRunning = sync.isPending || isSyncing; // spin (not while merely paused)
  const incrementalLabel = (running: boolean) =>
    running ? t('accounting.syncing') : paused ? t('accounting.retrying') : t('accounting.syncNow');

  // While a sync is in flight the lastSyncAt column is stale (it only updates
  // when all entities finish), so show live state instead.
  let lastSync: string;
  if (isSyncing) {
    lastSync =
      status.totalEntities && status.totalEntities > 0
        ? t('accounting.syncProgress', {
            done: status.doneEntities ?? 0,
            total: status.totalEntities,
          })
        : t('accounting.syncInProgress');
  } else if (paused) {
    lastSync = t('accounting.syncPaused', {
      when: status.nextAttemptAt
        ? new Date(status.nextAttemptAt).toLocaleString()
        : '…',
    });
  } else if (status.lastSyncAt) {
    const ts = new Date(status.lastSyncAt).toLocaleString();
    // Surface the last run's outcome so a successful incremental that changed
    // little doesn't look like a no-op / fake sync.
    if (status.status === 'COMPLETED') {
      const rows = status.rowsSynced ?? 0;
      const outcome =
        rows > 0
          ? t('accounting.recordsSynced', { count: rows })
          : t('accounting.upToDate');
      lastSync = `${ts} · ${outcome}`;
    } else {
      lastSync = ts;
    }
  } else {
    lastSync = t('accounting.neverSynced');
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" strokeWidth={1.8} />
            <div>
              <CardTitle className="text-base">
                {status.provider} — {status.companyName}
              </CardTitle>
              <p className="text-sm text-surface-500 mt-0.5 flex items-center gap-1.5">
                {isSyncing && (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin shrink-0" strokeWidth={1.8} />
                )}
                <span>
                  {t('accounting.lastSync')}: {lastSync}
                </span>
              </p>
            </div>
          </div>
          {status.entityCount != null && (
            <Badge variant="secondary">{t('accounting.entityCount', { count: status.entityCount })}</Badge>
          )}
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="pt-4">
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => sync.mutate(false)}
            disabled={busy}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${activeKind === 'INCREMENTAL' && activelyRunning ? 'animate-spin' : ''}`}
              strokeWidth={1.8}
            />
            {activeKind === 'INCREMENTAL'
              ? incrementalLabel(activelyRunning)
              : t('accounting.syncNow')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => sync.mutate(true)}
            disabled={busy}
            title={t('accounting.resyncAllHint')}
          >
            <RefreshCcw
              className={`w-4 h-4 mr-2 ${activeKind === 'FULL' && activelyRunning ? 'animate-spin' : ''}`}
              strokeWidth={1.8}
            />
            {activeKind === 'FULL'
              ? activelyRunning
                ? t('accounting.syncing')
                : t('accounting.retrying')
              : t('accounting.resyncAll')}
          </Button>
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogTrigger asChild>
              {/* Outline (not filled-destructive) so the destructive action
                  isn't the visual focal point; the confirm is gated below. */}
              <Button variant="outline" size="sm" disabled={disconnect.isPending}>
                <Unlink className="w-4 h-4 mr-2" strokeWidth={1.8} />
                {t('accounting.disconnect')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t('accounting.disconnectConfirmTitle', { provider: status.provider })}
                </DialogTitle>
                <DialogDescription>
                  {/* Reuse the pluralized entityCount key for the record phrase so
                      it reads "883 entities" / "1 entity" — grammatically correct
                      and consistent with the card's badge. */}
                  {t('accounting.disconnectConfirmBody', {
                    provider: status.provider,
                    records: t('accounting.entityCount', { count: status.entityCount ?? 0 }),
                  })}
                </DialogDescription>
              </DialogHeader>
              {disconnect.isError && (
                <p className="text-sm text-destructive">
                  {disconnect.error instanceof Error
                    ? disconnect.error.message
                    : t('accounting.disconnectFailed')}
                </p>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disconnect.isPending}
                  onClick={() => setConfirmOpen(false)}
                >
                  {t('accounting.disconnectConfirmCancel')}
                </Button>
                {/* Don't close inline: on success the connection flips to
                    disconnected and this whole card unmounts (dialog goes with
                    it); on error the dialog stays open showing the message above
                    and the button re-enables for a retry. */}
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={disconnect.isPending}
                  onClick={() => {
                    if (status.provider) disconnect.mutate(status.provider);
                  }}
                >
                  <Unlink className="w-4 h-4 mr-2" strokeWidth={1.8} />
                  {disconnect.isPending
                    ? t('accounting.disconnecting')
                    : t('accounting.disconnectConfirmAction')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}

function DisconnectedState() {
  const { t } = useTranslation('settings');
  const connectQBO = useConnectQBO();
  const connectXero = useConnectXero();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('accounting.connectTitle')}</CardTitle>
        <p className="text-sm text-surface-500">
          {t('accounting.connectDescription')}
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => connectQBO.mutate()}
            disabled={connectQBO.isPending}
          >
            <Link2 className="w-4 h-4 mr-2" strokeWidth={1.8} />
            {t('accounting.connectQBO')}
          </Button>
          <Button
            variant="outline"
            onClick={() => connectXero.mutate()}
            disabled={connectXero.isPending}
          >
            <Link2 className="w-4 h-4 mr-2" strokeWidth={1.8} />
            {t('accounting.connectXero')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function AccountingTab() {
  const { data: status, isLoading } = useAccountingStatus();
  // When a sync finishes, refresh the synced-data queries (e.g. the
  // Payment-adjustments account picker) so they reflect the new chart of accounts.
  useRefreshOnSyncComplete();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Only show payment-adjustment settings to orgs with an active connection
  // — the picker reads from the synced chart of accounts.
  if (status?.connected) {
    return (
      <div className="space-y-6">
        <ConnectedState />
        <PaymentSettingsCard />
      </div>
    );
  }
  return <DisconnectedState />;
}
